(function() {
    "use strict";

    if (customElements.get("collection-filters")) return;

    customElements.define("collection-filters", class extends HTMLElement {
        constructor() {
            super();

            // Event Bindings
            this.sortDropdownEvent = () => this.sortDropdownToggle();
            this.onTabHandlerEvent = (event) => this.onTabHandler(event);
            this.updateCollectionFormSortEvent = (event) => this.updateCollectionFormSort(event);
            this.bodyClickEvent = (event) => this.bodyClick(event);

            this.onFilterResetClick = this.onFilterResetClick.bind(this);
            this.onFilterTagResetClick = this.onFilterTagResetClick.bind(this);
            this.onFilterTagClearClick = this.onFilterTagClearClick.bind(this);
            this.onFilterToggleClick = this.onFilterToggleClick.bind(this);
            this.onKeyUpHandler = this.onKeyUpHandler.bind(this);
            this.updateRangeEvent = this.updateRange.bind(this);
            this.onHistoryChangeBound = this.onHistoryChange.bind(this);

            // Debounced Events
            this.debouncedSubmitEvent = window.theme.debounce((event) => {
                this.onSubmitHandler(event);
            }, 500);

            this.debouncedSortEvent = window.theme.debounce((event) => {
                this.onSortChange(event);
            }, 500);
        }

        connectedCallback() {
            this.container = this.closest("[data-section-id]");
            this.sectionId = this.container.dataset.sectionId;
            this.enableFilters = this.container.dataset.enableFilters === "true";
            this.enableSorting = this.container.dataset.enableSorting === "true";
            this.filterMode = this.container.dataset.filterMode;
            this.collectionHandle = this.container.dataset.collection;
            this.isSearchPage = this.container.closest("[data-search-performed]") != null;

            // DOM Elements
            this.productGrid = this.container.querySelector("[data-collection-products]");
            this.productsCount = this.container.querySelector("[data-products-count]");
            this.groupTagFilters = this.container.querySelectorAll("[data-collection-filter]");
            this.filters = this.container.querySelector("[data-collection-filters]");
            this.filterTriggers = this.container.querySelectorAll("[data-collapsible-trigger]");
            this.filtersStickyBar = this.container.querySelector("[data-collection-sticky-bar]");
            this.filtersForm = this.container.querySelector("[data-collection-filters-form]");
            this.inputSort = this.container.querySelectorAll("[data-input-sort]");
            this.sortToggle = this.container.querySelector("[data-sort-toggle]");
            this.collectionSortOptions = this.container.querySelector("[data-collection-sort-options]");
            this.ajaxinateItems = this.container.querySelector("ajaxinate-items[data-collection-products]");

            this.a11y = window.theme.a11y;
            this.filterData = [];
            this.isSortOnlyChange = false;

            if (this.filters) {
                this.hideFiltersDrawer = this.hideFiltersDrawer.bind(this);
                this.showFiltersDrawer = this.showFiltersDrawer.bind(this);
                this.resizeEvent = window.theme.debounce(() => {
                    this.filtersResizeEvents();
                }, 500);
                this.filtersResizeEvents();
                document.addEventListener("theme:resize:width", this.resizeEvent);
            }

            this.initTagFilters();
            window.addEventListener("popstate", this.onHistoryChangeBound);
            this.bindToggleButtonsEvents();
            this.bindFilterButtonsEvents();
            this.initProductGridEvents();
            window.theme.setVars();

            this.sortToggle?.addEventListener("click", this.sortDropdownEvent);
            document.addEventListener("click", this.bodyClickEvent);

            this.filterShowMore();
        }

        handleVisibleTooltips() {
            const tooltip = document.querySelector("[data-tooltip-container]");
            if (tooltip?.classList.contains("is-visible")) {
                tooltip.classList.remove("is-visible");
            }
        }

        updateRange() {
            const minSlider = this.filtersForm.querySelector("[data-se-min-value]");
            const maxSlider = this.filtersForm.querySelector("[data-se-max-value]");
            const minInput = this.filtersForm.querySelector("[data-field-price-min]");
            const maxInput = this.filtersForm.querySelector("[data-field-price-max]");

            if (minSlider.hasAttribute("data-se-min-value") && maxSlider.hasAttribute("data-se-max-value")) {
                const placeholderMin = parseFloat(minInput.placeholder, 10);
                const placeholderMax = parseFloat(maxInput.placeholder, 10);
                const currentMin = parseFloat(minSlider.getAttribute("data-se-min-value"), 10);
                const currentMax = parseFloat(maxSlider.getAttribute("data-se-max-value"), 10);

                if (placeholderMin !== currentMin || placeholderMax !== currentMax) {
                    minInput.value = parseInt(currentMin);
                    maxInput.value = parseInt(currentMax);
                    this.filtersForm.dispatchEvent(new Event("input", { bubbles: true }));
                }
            }
        }

        onSubmitHandler(event) {
            event.preventDefault();
            const formData = new FormData(this.filtersForm);
            const searchParams = new URLSearchParams(formData);
            let omittedParams = [];
            let queryString = "";

            if (this.isSearchPage) {
                this.searchForm = this.container.querySelector("[data-search-form]");
                this.currentType = this.container.getAttribute("data-current-type");
            }

            // Handle Price Range resets if they match defaults
            const minSlider = this.filtersForm.querySelector("[data-se-min-value]");
            const maxSlider = this.filtersForm.querySelector("[data-se-max-value]");
            const minInput = this.filtersForm.querySelector("[data-field-price-min]");
            const maxInput = this.filtersForm.querySelector("[data-field-price-max]");

            if (minSlider && maxSlider && minInput && maxInput && minSlider.hasAttribute("data-se-min") && maxSlider.hasAttribute("data-se-max")) {
                const defaultMin = parseFloat(minSlider.getAttribute("data-se-min"), 10);
                const defaultMax = parseFloat(maxSlider.getAttribute("data-se-max"), 10);
                const currentMin = minInput.value ? parseFloat(minInput.value, 10) : defaultMin;
                const currentMax = maxInput.value ? parseFloat(maxInput.value, 10) : defaultMax;

                if (currentMin <= defaultMin && currentMax >= defaultMax) {
                    omittedParams.push("filter.v.price.gte");
                    omittedParams.push("filter.v.price.lte");
                    searchParams.delete("filter.v.price.gte");
                    searchParams.delete("filter.v.price.lte");
                }
            }

            queryString = searchParams.toString();

            if (this.isSearchPage) {
                queryString = this.combineSearchParams(this.searchForm, this.filtersForm, omittedParams);
                let typeParam = "";
                if (this.currentType === "all") typeParam = "&type=product";
                if (queryString.indexOf("&type=product") > -1) typeParam = "";
                queryString += typeParam;
            }

            this.renderSection(queryString, event);
        }

        combineSearchParams(formA, formB, excludeList = [], typeOverride = false) {
            const dataA = new FormData(formA);
            const paramsA = new URLSearchParams(dataA);
            const dataB = new FormData(formB);
            const paramsB = new URLSearchParams(dataB);
            const emptyKeys = [];

            for (const entry of dataA.entries()) if (entry[1] === "") emptyKeys.push(entry[0]);
            for (const entry of dataB.entries()) if (entry[1] === "") emptyKeys.push(entry[0]);

            emptyKeys.forEach(key => {
                if (paramsA.has(key)) paramsA.delete(key);
                if (paramsB.has(key)) paramsB.delete(key);
            });

            for (const key of paramsA.keys()) {
                if (paramsB.has(key)) paramsB.delete(key);
            }

            excludeList.forEach(key => {
                if (paramsA.has(key)) paramsA.delete(key);
                if (paramsB.has(key)) paramsB.delete(key);
            });

            if (typeOverride) {
                if (paramsB.has("type")) paramsB.delete("type");
                paramsA.set("type", typeOverride);
            }

            return `${paramsA.toString()}&${paramsB.toString()}`;
        }

        onHistoryChange(event) {
            if (!this.filters) return;
            let params = event.state?.searchParams || "";

            if (this.isSearchPage) {
                if (!event.state) params = window.location.search;
                if (!(params.indexOf("type=product") > -1)) return;
            }

            this.renderSection(params, null, false);
        }

        renderSection(params, event, updateHash = true) {
            this.startLoading();
            const url = `${window.location.pathname}?section_id=${this.sectionId}&${params}`;
            const isCached = (item) => item.url === url;

            if (this.filterData.some(isCached)) {
                this.renderSectionFromCache(isCached, event);
            } else {
                this.renderSectionFromFetch(url, event);
            }

            if (updateHash) this.updateURLHash(params);
        }

        renderSectionFromFetch(url) {
            fetch(url)
                .then(response => response.text())
                .then(html => {
                    this.filterData = [...this.filterData, { html: html, url: url }];
                    this.inputSort = this.container.querySelectorAll("[data-input-sort]");
                    this.renderFilters(html);
                    this.bindFilterButtonsEvents();
                    this.hideFiltersOnMobile();
                    this.renderProductGrid(html);
                    this.updateProductsCount(html);
                    this.finishLoading();
                    this.mobileFiltersScrollLock();
                });
        }

        renderSectionFromCache(predicate, event) {
            const cachedHtml = this.filterData.find(predicate).html;
            this.renderFilters(cachedHtml, event);
            this.hideFiltersOnMobile();
            this.renderProductGrid(cachedHtml);
            this.updateProductsCount(cachedHtml);
            this.finishLoading();
            this.mobileFiltersScrollLock();
        }

        renderProductGrid(html) {
            const parsed = new DOMParser().parseFromString(html, "text/html");
            const newGrid = parsed.querySelector("[data-collection-products]");
            if (newGrid) {
                this.productGrid.innerHTML = newGrid.innerHTML;
                this.reInitAjaxinate(newGrid);
                this.initProductGridEvents();
                this.filterShowMore();
            }
        }

        reInitAjaxinate(gridElement) {
            if (gridElement.matches("ajaxinate-items") && typeof this.ajaxinateItems?.reinit === "function") {
                this.ajaxinateItems.reinit();
            }
        }

        initProductGridEvents() {
            this.handleVisibleTooltips();
            setTimeout(() => { this.finishLoading(); }, 450);
        }

        updateProductsCount(html) {
            const parsed = new DOMParser().parseFromString(html, "text/html");
            const newCount = parsed.querySelector("[data-products-count]");
            if (newCount) {
                this.productsCount.innerHTML = newCount.innerHTML;
            }
        }

        renderFilters(html) {
            const parsed = new DOMParser().parseFromString(html, "text/html");
            const newFilters = parsed.querySelector("[data-collection-filters]");
            if (newFilters) {
                this.filters.innerHTML = newFilters.innerHTML;
                this.filtersForm = document.querySelector("[data-collection-filters-form]");
                this.inputSort = this.container.querySelectorAll("[data-input-sort]");
                this.sortToggle = this.container.querySelector("[data-sort-toggle]");
                this.collectionSortOptions = this.container.querySelector("[data-collection-sort-options]");
                this.bindFilterButtonsEvents();
                this.bindToggleButtonsEvents();
            }
        }

        updateURLHash(params) {
            const url = `${window.location.pathname}${params ? "?" + params : ""}`;
            history.pushState({ searchParams: params }, "", url);
        }

        bindFilterButtonsEvents() {
            if (this.inputSort.length > 0) {
                this.inputSort.forEach(input => {
                    input.addEventListener("change", this.updateCollectionFormSortEvent);
                });
            }

            if (this.filtersForm) {
                this.filtersForm.addEventListener("input", this.debouncedSubmitEvent.bind(this));
                this.filtersForm.addEventListener("theme:filter:range-update", this.updateRangeEvent);
            }

            if (this.collectionSortOptions) {
                this.collectionSortOptions.addEventListener("keyup", this.onTabHandlerEvent);
            }

            if (this.filterMode !== "tag" && this.filterMode !== "group" && this.enableFilters) {
                this.container.querySelectorAll("[data-filter-reset-button]").forEach(btn => {
                    btn.addEventListener("click", this.onFilterResetClick, { once: true });
                });
            }
        }

        onFilterResetClick(event) {
            event.preventDefault();
            const params = new URL(event.currentTarget.href).searchParams.toString();
            this.renderSection(params);
        }

        bindToggleButtonsEvents() {
            this.container.querySelectorAll("[data-toggle-filters]").forEach(btn => {
                btn.addEventListener("click", this.onFilterToggleClick);
            });
            this.container.querySelectorAll("[data-close-filters]").forEach(btn => {
                btn.addEventListener("click", this.hideFiltersDrawer);
            });
            this.container.querySelectorAll("[data-open-filters]").forEach(btn => {
                btn.addEventListener("click", this.showFiltersDrawer);
            });
            this.container.querySelector("[data-collection-wrapper]")?.addEventListener("keyup", this.onKeyUpHandler);
        }

        onTabHandler(event) {
            const keys = theme.keyboardKeys;
            if (event.code === keys.SPACE || event.code === keys.ENTER || event.code === keys.NUMPADENTER) {
                const val = event.target.previousElementSibling.value;
                this.filtersForm.querySelectorAll("[data-input-sort]").forEach(input => {
                    input.checked = (input.value === val);
                });
                this.filtersForm.dispatchEvent(new Event("input", { bubbles: true }));
                event.target.dispatchEvent(new Event("click", { bubbles: true }));
            }
        }

        onKeyUpHandler(event) {
            if (event.code === theme.keyboardKeys.ESCAPE) {
                this.hideFiltersDrawer();
            }
        }

        onFilterToggleClick(event) {
            event.preventDefault();
            window.theme.setVars();
            this.filters.classList.contains("collection__filters--visible") ? 
                this.hideFiltersDrawer() : 
                this.showFiltersDrawer();
        }

        sortDropdownToggle() {
            this.collectionSortOptions?.classList.toggle("collection__sort__option-wrapper--visible");
        }

        bodyClick(event) {
            if (!this.collectionSortOptions) return;
            const isClickInsideToggle = this.sortToggle.contains(event.target);
            const isVisible = this.collectionSortOptions.classList.contains("collection__sort__option-wrapper--visible");
            if (isVisible && !isClickInsideToggle) {
                this.sortDropdownToggle();
            }
        }

        updateCollectionFormSort(event) {
            const value = event.target.value;
            const optionsWrapper = event.target.closest("[data-collection-sort-options]");
            this.container.querySelectorAll("[data-input-sort]").forEach(input => {
                if (input.value === value) input.checked = true;
            });
            this.isSortOnlyChange = true;
            if (optionsWrapper !== null) {
                this.filtersForm.dispatchEvent(new Event("input", { bubbles: true }));
            }
        }

        showFiltersDrawer() {
            this.a11y.state.trigger = document.querySelector("[data-toggle-filters]");
            this.a11y.trapFocus({ container: this.filters });
            this.mobileFiltersScrollLock();
        }

        mobileFiltersScrollLock() {
            if (window.innerWidth < theme.sizes.small) {
                // Don't open drawer for sort-only changes
                if (this.isSortOnlyChange) {
                    this.isSortOnlyChange = false;
                    this.hideFiltersDrawer();
                    return;
                }
                
                const filterList = document.querySelector("[data-collection-filters-list]");
                if (!this.filters.classList.contains("collection__filters--visible")) {
                    this.filters.classList.add("collection__filters--visible");
                }
                document.dispatchEvent(new CustomEvent("theme:scroll:lock", {
                    bubbles: true,
                    detail: filterList
                }));
            }
        }

        hideFiltersOnMobile() {
            const triggers = this.container.querySelectorAll("[data-collapsible-trigger]:not([data-show-more-trigger])");
            if (window.innerWidth < theme.sizes.small) {
                requestAnimationFrame(() => {
                    triggers.forEach(trigger => {
                        const isActive = trigger.getAttribute("data-filter-active") === "true";
                        if (trigger.classList.contains("is-expanded") && !isActive) {
                            trigger.dispatchEvent(new Event("click"));
                        }
                    });
                });
            }
        }

        showFiltersOnDesktop() {
            const triggers = this.container.querySelectorAll("[data-collapsible-trigger]:not([data-show-more-trigger])");
            const defaultState = this.container.getAttribute("data-filters-default-state");
            const isFirstOpen = defaultState === "first-open";
            const isOpen = defaultState === "open";
            const isClosed = defaultState === "closed";
            const sortOffset = this.enableSorting ? 1 : 0;

            triggers.forEach((trigger, index) => {
                const isExpanded = trigger.classList.contains("is-expanded");
                const isActive = trigger.getAttribute("data-filter-active") === "true";
                const shouldBeOpen = (isActive && !isExpanded && isOpen) ||
                                     (isClosed && isExpanded) ||
                                     (isFirstOpen && (!isExpanded && index === sortOffset)) ||
                                     (isFirstOpen && (isExpanded && index !== sortOffset)) ||
                                     (isOpen && !isExpanded);

                if (shouldBeOpen) trigger.dispatchEvent(new Event("click"));
            });
        }

        hideFiltersDrawer() {
            const isVisible = this.filters.classList.contains("collection__filters--visible");
            const isLoading = this.container.classList.contains("is-loading");
            if (isVisible) {
                this.filters.classList.remove("collection__filters--visible");
                this.a11y.removeTrapFocus();
            }
            if (!isLoading) {
                document.dispatchEvent(new CustomEvent("theme:scroll:unlock", {
                    bubbles: true,
                    detail: 300
                }));
            }
        }

        filtersResizeEvents() {
            if (window.innerWidth >= theme.sizes.small) {
                this.showFiltersOnDesktop();
                this.hideFiltersDrawer();
            } else {
                this.hideFiltersOnMobile();
            }
        }

        filterShowMore() {
            this.showMore = this.container.querySelectorAll("[data-show-more]");
            if (this.showMore.length === 0) return;

            this.showMore.forEach(item => {
                const trigger = item.querySelector("[data-collapsible-trigger]");
                const actions = item.querySelector("[data-show-more-actions]");
                if (!actions) return;

                const moreBtn = actions.querySelector("[data-show-more-trigger]");
                const container = actions.querySelector("[data-show-more-container]");
                const focusables = container.querySelectorAll(window.theme.focusable);

                if (container.getAttribute("aria-expanded") !== "true") {
                    focusables.forEach(el => el.setAttribute("tabindex", "-1"));
                }

                moreBtn.addEventListener("keyup", (e) => {
                    const keys = theme.keyboardKeys;
                    if ([keys.SPACE, keys.ENTER, keys.NUMPADENTER].includes(e.code)) {
                        this.updateShowMoreFocusableElements(e, focusables);
                    }
                });

                moreBtn.addEventListener("click", (e) => this.updateShowMoreFocusableElements(e, focusables));

                trigger.addEventListener("keyup", (e) => {
                    const keys = theme.keyboardKeys;
                    if ([keys.SPACE, keys.ENTER, keys.NUMPADENTER].includes(e.code)) {
                        this.updateCollapsedContainerFocusableElements(trigger, moreBtn, focusables);
                    }
                });

                trigger.addEventListener("click", () => this.updateCollapsedContainerFocusableElements(trigger, moreBtn, focusables));
            });
        }

        updateCollapsedContainerFocusableElements(trigger, moreBtn, focusables) {
            requestAnimationFrame(() => {
                const isTriggerExpanded = trigger.getAttribute("aria-expanded") === "true";
                const isMoreExpanded = moreBtn.getAttribute("aria-expanded") === "true";
                focusables.forEach(el => {
                    if (!isTriggerExpanded && isMoreExpanded) el.setAttribute("tabindex", "-1");
                    if (isTriggerExpanded && isMoreExpanded) el.removeAttribute("tabindex");
                });
            });
        }

        updateShowMoreFocusableElements(event, focusables) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const isExpanded = event.target.getAttribute("aria-expanded") === "true";
                    focusables.forEach((el, index) => {
                        if (isExpanded) {
                            el.removeAttribute("tabindex");
                            if (index === 0) el.focus();
                        } else {
                            el.setAttribute("tabindex", "-1");
                        }
                    });
                });
            });
        }

        // --- Tag Filtering Logic ---
        initTagFilters() {
            if ((this.filterMode === "tag" || this.filterMode === "group") && this.enableFilters) {
                this.tags = this.container.dataset.tags.split("+").filter(tag => tag);
                this.bindFilterTagButtonsEvents();
                this.bindSortChangeEvent();
            }
        }

        renderTagFiltersProducts(url, onlySortChange = false) {
            this.startLoading();
            fetch(url)
                .then(res => res.text())
                .then(html => {
                    const parsed = new DOMParser().parseFromString(html, "text/html");
                    const products = parsed.querySelector("[data-collection-products]");
                    const filters = parsed.querySelector("[data-collection-filters]");

                    this.productGrid.innerHTML = products.innerHTML;
                    this.filters.innerHTML = filters.innerHTML;
                    this.inputSort = this.container.querySelectorAll("[data-input-sort]");
                    this.filtersForm = document.querySelector("[data-collection-filters-form]");
                    this.filterData = [...this.filterData, { html: html, url: url }];
                    
                    this.alreadyClicked = false;
                    this.bindFilterTagButtonsEvents();
                    this.bindFilterButtonsEvents();
                    this.bindSortChangeEvent();
                    this.bindToggleButtonsEvents();
                    this.reInitAjaxinate(products);
                    this.initProductGridEvents();
                    this.updateProductsCount(html);

                    this.isSortOnlyChange = onlySortChange;
                    this.mobileFiltersScrollLock();
                    this.hideFiltersOnMobile();
                    this.filterShowMore();

                    if (history.replaceState) window.history.pushState({ path: url }, "", url);
                })
                .catch(err => {
                    this.finishLoading();
                    console.log(`Error: ${err}`);
                });
        }

        bindFilterTagButtonsEvents() {
            this.container.querySelectorAll("[data-collection-filter-tag-button]").forEach(btn => {
                btn.addEventListener("click", this.onFilterTagButtonClick.bind(this));
            });
            this.container.querySelectorAll("[data-filter-tag-reset-button]").forEach(btn => {
                btn.addEventListener("click", this.onFilterTagClearClick);
            });
            this.container.querySelectorAll("[data-filter-reset-button]").forEach(btn => {
                btn.addEventListener("click", this.onFilterTagResetClick);
            });
        }

        bindSortChangeEvent() {
            this.container.querySelectorAll("[data-input-sort]").forEach(input => {
                input.addEventListener("input", this.debouncedSortEvent.bind(this));
            });
        }

        onFilterTagButtonClick(event) {
            event.preventDefault();
            if (this.alreadyClicked) return;
            this.alreadyClicked = true;

            const btn = event.currentTarget;
            const tag = btn.dataset.tag;
            const parent = btn.parentNode;

            if (parent.classList.contains("is-active")) {
                let index = this.tags.indexOf(tag);
                parent.classList.remove("is-active");
                if (index > -1) this.tags.splice(index, 1);
            } else {
                parent.classList.add("is-active");
                this.tags.push(tag);
            }

            let newUrl = `${this.collectionHandle}/${this.tags.join("+")}?sort_by=${this.getSortValue()}`;
            
            const groupFilter = this.container.querySelector("[data-collection-filter]");
            groupFilter.classList.remove("is-expanded");
            groupFilter.setAttribute("aria-expanded", false);
            this.container.setAttribute("data-tags", `[${this.tags}]`);
            
            this.renderTagFiltersProducts(newUrl);
        }

        onFilterTagClearClick(event) {
            event.preventDefault();
            if (this.alreadyClicked) return;
            this.alreadyClicked = true;

            const tag = event.currentTarget.dataset.tag;
            const index = this.tags.indexOf(tag);
            if (index > -1) this.tags.splice(index, 1);

            const newUrl = `${this.collectionHandle}/${this.tags.join("+")}?sort_by=${this.getSortValue()}`;
            this.container.setAttribute("data-tags", `[${this.tags}]`);
            this.renderTagFiltersProducts(newUrl);
        }

        onSortChange() {
            let url = `${this.collectionHandle}/${this.tags.join("+")}?sort_by=${this.getSortValue()}`;
            this.renderTagFiltersProducts(url, true);
        }

        getSortValue() {
            let val = "";
            this.inputSort.forEach(input => {
                if (input.checked) val = input.value;
            });
            return val;
        }

        onFilterTagResetClick(event) {
            event?.preventDefault();
            if (this.alreadyClicked) return;
            this.alreadyClicked = true;

            this.container.querySelectorAll("[data-collection-filter-tag]").forEach(el => el.classList.remove("is-active"));
            this.container.querySelectorAll("[data-collection-filter]").forEach(el => {
                el.classList.remove("is-expanded");
                el.setAttribute("aria-expanded", false);
            });

            this.tags = [];
            this.container.setAttribute("data-tags", "");
            let url = `${this.collectionHandle}/?sort_by=${this.getSortValue()}`;
            this.renderTagFiltersProducts(url);
        }

        // --- Loading & UI States ---
        getProductsOffsetTop() {
            const gridTop = this.productGrid.getBoundingClientRect().top;
            const bodyTop = document.body.getBoundingClientRect().top;
            const stickyHeight = this.filtersStickyBar.offsetHeight;
            return gridTop - bodyTop - stickyHeight;
        }

        startLoading() {
            this.container.classList.add("is-loading");
            if (window.innerWidth >= theme.sizes.small) {
                document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true }));
            }
            let offset = this.getProductsOffsetTop();
            window.scrollTo({ top: offset, left: 0, behavior: "smooth" });
        }

        finishLoading() {
            const activePopups = document.querySelectorAll('[data-section-type="popups"] .popup--visible');
            const hasPopups = activePopups.length > 0;
            this.container.classList.remove("is-loading");

            if (hasPopups) {
                let preventLockCount = 0;
                [...activePopups].forEach(p => {
                    if (p.hasAttribute("data-prevent-scroll-lock")) preventLockCount++;
                });
                if (preventLockCount === activePopups.length) {
                    document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true, detail: 300 }));
                }
            } else if (window.innerWidth >= theme.sizes.small) {
                document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true, detail: 300 }));
            }
        }

        disconnectedCallback() {
            if (this.filters) document.removeEventListener("theme:resize:width", this.resizeEvent);
            window.removeEventListener("popstate", this.onHistoryChangeBound);
            document.removeEventListener("click", this.bodyClickEvent);
            if (this.groupTagFilters.length > 0) this.onFilterTagResetClick();
            this.finishLoading();
        }
    });
})();